<template>
  <div id="blocklyParent">
    <div id="blocklyDisableOverlay" v-show="readonly"></div>
    <div id="errorOverlay" v-show="encounteredRestoreError">
      <div class="ui negative message">
        <div class="header">{{ $t('BLOCKLY_BAD_XML_HEADER') }}</div>
        <p>{{ $t('BLOCKLY_BAD_XML_MESSAGE') }}</p>
        <p>{{ encounteredRestoreError }}</p>
      </div>
    </div>
    <div id="blocklyDiv" ref="workspace" v-show="!encounteredRestoreError"></div>
    <xml id="toolbox" ref="toolbox" xmlns="http://www.w3.org/1999/xhtml" style="display: none;">
      <category name="Logic" colour="#5C81A6">
        <block type="controls_if"></block>
        <block type="logic_compare"><field name="OP">EQ</field></block>
        <block type="javascript_logic_operation"><field name="OP">AND</field></block>
        <block type="logic_negate"></block>
        <block type="logic_boolean"><field name="BOOL">TRUE</field></block>
        <block type="logic_null"></block>
        <block type="logic_ternary"></block>
        <block type="exceptions_try_catch">
          <value name="errorVariable">
            <shadow type="text"><field name="TEXT">e</field></shadow>
          </value>
        </block>
        <block type="javascript_throw">
          <value name="MESSAGE">
            <shadow type="text"><field name="TEXT"></field></shadow>
          </value>
        </block>
      </category>
      <category name="Loops" colour="#5CA65C">
        <block type="controls_repeat_ext">
          <value name="TIMES">
            <shadow type="math_number"><field name="NUM">10</field></shadow>
          </value>
        </block>
        <block type="controls_whileUntil"><field name="MODE">WHILE</field></block>
        <block type="javascript_for_loop"></block>
        <block type="javascript_for_of_loop"></block>
        <block type="controls_flow_statements"><field name="FLOW">BREAK</field></block>
      </category>
      <category name="Math" colour="#5C68A6">
        <block type="math_round">
          <field name="OP">ROUND</field>
          <value name="NUM">
            <shadow type="math_number"><field name="NUM">3.1</field></shadow>
          </value>
        </block>
        <block type="math_number"><field name="NUM">0</field></block>
        <block type="math_single">
          <field name="OP">ROOT</field>
          <value name="NUM">
            <shadow type="math_number"><field name="NUM">9</field></shadow>
          </value>
        </block>
        <block type="math_trig">
          <field name="OP">SIN</field>
          <value name="NUM">
            <shadow type="math_number"><field name="NUM">45</field></shadow>
          </value>
        </block>
        <block type="math_constant"><field name="CONSTANT">PI</field></block>
        <block type="math_number_property">
          <mutation divisor_input="false"></mutation>
          <field name="PROPERTY">EVEN</field>
          <value name="NUMBER_TO_CHECK">
            <shadow type="math_number"><field name="NUM">0</field></shadow>
          </value>
        </block>
        <block type="math_arithmetic">
          <field name="OP">ADD</field>
          <value name="A">
            <shadow type="math_number"><field name="NUM">1</field></shadow>
          </value>
          <value name="B">
            <shadow type="math_number"><field name="NUM">1</field></shadow>
          </value>
        </block>
        <block type="math_on_list">
          <mutation op="SUM"></mutation>
          <field name="OP">SUM</field>
        </block>
        <block type="math_modulo">
          <value name="DIVIDEND">
            <shadow type="math_number"><field name="NUM">64</field></shadow>
          </value>
          <value name="DIVISOR">
            <shadow type="math_number"><field name="NUM">10</field></shadow>
          </value>
        </block>
        <block type="math_constrain">
          <value name="VALUE">
            <shadow type="math_number"><field name="NUM">50</field></shadow>
          </value>
          <value name="LOW">
            <shadow type="math_number"><field name="NUM">1</field></shadow>
          </value>
          <value name="HIGH">
            <shadow type="math_number"><field name="NUM">100</field></shadow>
          </value>
        </block>
        <block type="math_random_int">
          <value name="FROM">
            <shadow type="math_number"><field name="NUM">1</field></shadow>
          </value>
          <value name="TO">
            <shadow type="math_number"><field name="NUM">100</field></shadow>
          </value>
        </block>
        <block type="math_random_float"></block>
      </category>
      <category name="Text" colour="#5CA68D">
        <block type="text_charAt">
          <mutation at="true"></mutation>
          <field name="WHERE">FROM_START</field>
        </block>
        <block type="text"><field name="TEXT"></field></block>
        <block type="text_append">
          <field name="VAR" id=";L.b6z+HbfKD1XwHCC-1">item</field>
          <value name="TEXT">
            <shadow type="text"><field name="TEXT"></field></shadow>
          </value>
        </block>
        <block type="text_length">
          <value name="VALUE">
            <shadow type="text"><field name="TEXT">abc</field></shadow>
          </value>
        </block>
        <block type="text_isEmpty">
          <value name="VALUE">
            <shadow type="text"><field name="TEXT"></field></shadow>
          </value>
        </block>
        <block type="text_indexOf">
          <field name="END">FIRST</field>
          <value name="FIND">
            <shadow type="text"><field name="TEXT">abc</field></shadow>
          </value>
        </block>
        <block type="text_join"><mutation items="2"></mutation></block>
        <block type="text_getSubstring">
          <mutation at1="true" at2="true"></mutation>
          <field name="WHERE1">FROM_START</field>
          <field name="WHERE2">FROM_START</field>
        </block>
        <block type="text_changeCase">
          <field name="CASE">UPPERCASE</field>
          <value name="TEXT">
            <shadow type="text"><field name="TEXT">abc</field></shadow>
          </value>
        </block>
        <block type="text_trim">
          <field name="MODE">BOTH</field>
          <value name="TEXT">
            <shadow type="text"><field name="TEXT">abc</field></shadow>
          </value>
        </block>
        <block type="text_print">
          <value name="TEXT">
            <shadow type="text"><field name="TEXT">abc</field></shadow>
          </value>
        </block>
        <block type="text_prompt_ext">
          <mutation type="TEXT"></mutation>
          <field name="TYPE">TEXT</field>
          <value name="TEXT">
            <shadow type="text"><field name="TEXT">abc</field></shadow>
          </value>
        </block>
        <block type="javascript_newline"></block>
      </category>
      <category name="Lists" colour="#745CA6">
        <block type="lists_indexOf"><field name="END">FIRST</field></block>
        <block type="lists_create_with"><mutation items="0"></mutation></block>
        <block type="lists_repeat">
          <value name="NUM">
            <shadow type="math_number"><field name="NUM">5</field></shadow>
          </value>
        </block>
        <block type="lists_length"></block>
        <block type="lists_isEmpty"></block>
        <block type="lists_create_with"><mutation items="3"></mutation></block>
        <block type="lists_getIndex">
          <mutation statement="false" at="true"></mutation>
          <field name="MODE">GET</field>
          <field name="WHERE">FROM_START</field>
        </block>
        <block type="lists_setIndex">
          <mutation at="true"></mutation>
          <field name="MODE">SET</field>
          <field name="WHERE">FROM_START</field>
        </block>
        <block type="lists_getSublist">
          <mutation at1="true" at2="true"></mutation>
          <field name="WHERE1">FROM_START</field>
          <field name="WHERE2">FROM_START</field>
        </block>
        <block type="lists_split">
          <mutation mode="SPLIT"></mutation>
          <field name="MODE">SPLIT</field>
        </block>
        <block type="lists_sort">
          <field name="TYPE">NUMERIC</field>
          <field name="DIRECTION">1</field>
        </block>
      </category>
      <category name="Objects" colour="#a6995b">
        <block type="javascript_object_literal"></block>
        <block type="javascript_property_get">
          <value name="OBJECT"><shadow type="javascript_variable_access"></shadow></value>
        </block>
        <block type="javascript_property_set">
          <value name="OBJECT"><shadow type="javascript_variable_access"></shadow></value>
        </block>
        <block type="javascript_computed_property_get">
          <value name="OBJECT"><shadow type="javascript_variable_access"></shadow></value>
          <value name="PROPERTY"><shadow type="javascript_variable_access"></shadow></value>
        </block>
        <block type="javascript_computed_property_set">
          <value name="OBJECT"><shadow type="javascript_variable_access"></shadow></value>
          <value name="PROPERTY"><shadow type="javascript_variable_access"></shadow></value>
        </block>
        <block type="javascript_object_new">
          <value name="function"><shadow type="javascript_variable_access"></shadow></value>
        </block>
      </category>
      <category name="Variables" colour="#A65C81">
        <block type="javascript_variable_declaration">
          <statement name="declarations">
            <block type="javascript_variable_assign"></block>
          </statement>
        </block>
        <block type="javascript_variable_assign"></block>
        <block type="javascript_variable_access"></block>
      </category>
      <category name="Functions" colour="#9A5CA6">
        <block type="javascript_function_invocation">
          <value name="function"><shadow type="javascript_variable_access"></shadow></value>
        </block>
        <block type="javascript_function_invocation_return">
          <value name="function"><shadow type="javascript_variable_access"></shadow></value>
        </block>
        <block type="javascript_function_return"></block>
        <block type="javascript_lambda_declaration"></block>
        <block type="javascript_function_declaration">
          <field name="arguments">x, y</field>
          <statement name="body">
            <block type="javascript_function_return">
              <value name="VALUE">
                <shadow type="javascript_variable_access">
                  <field name="variableName">x</field>
                </shadow>
              </value>
            </block>
          </statement>
        </block>
      </category>
      <category name="Other" colour="#5ba674">
        <block type="javascript_json_parse"></block>
        <block type="javascript_json_stringify"></block>
        <block type="javascript_arbitrary_statement"></block>
        <block type="javascript_arbitrary_expression"></block>
        <block type="javascript_expression_statement"></block>
        <block type="javascript_untyped_add"></block>
        <block type="javascript_literal_null"></block>
        <block type="javascript_literal_regexp"></block>
        <block type="javascript_unary_expression"></block>
      </category>
      <sep></sep>
      <category name="Flow inputs" custom="INPUTS_CATEGORY" :colour="apiColor"></category>
      <sep></sep>
      <category name="Automation" :colour="apiColor">
        <category name="App" :colour="apiColor">
          <block type="sirenia_api_app_navigate">
            <value name="URL">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
          </block>
          <block type="sirenia_api_app_location"></block>
          <block type="sirenia_api_app_session"></block>
          <block type="sirenia_api_app_quit"></block>
        </category>
        <category name="Debug" :colour="apiColor">
          <block type="sirenia_api_debug_dialog">
            <value name="MSG">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
          </block>
          <block type="sirenia_api_debugger"></block>
        </category>
        <category name="Dialogs" :colour="apiColor">
          <block type="javascript_expression_statement">
            <value name="ignoredInput">
              <block type="sirenia_api_dialog_info">
                <value name="HEADER">
                  <shadow type="text"><field name="TEXT"></field></shadow>
                </value>
                <value name="TEXT">
                  <shadow type="text"><field name="TEXT"></field></shadow>
                </value>
              </block>
            </value>
          </block>
          <block type="sirenia_api_dialog_button"></block>
          <block type="sirenia_api_dialog_input">
            <value name="HEADER">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
            <value name="TEXT">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
            <value name="INPUTS"><shadow type="javascript_object_literal"></shadow></value>
          </block>
          <block type="sirenia_api_dialog_html_input">
            <value name="HEADER">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
            <value name="TEXT">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
          </block>
        </category>
        <category name="Environment" :colour="apiColor">
          <block type="sirenia_api_env_username"></block>
          <block type="sirenia_api_env_machinename"></block>
          <block type="sirenia_api_env_userdomain"></block>
          <block type="sirenia_api_env_primaryscreen"></block>
          <block type="sirenia_api_env_screens"></block>
        </category>
        <category name="Excel" :colour="apiColor">
          <block type="sirenia_api_excel_load">
            <value name="PATH">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
          </block>
          <block type="sirenia_api_excel_delete_sheet">
            <value name="SHEET">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
            <value name="PATH">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
          </block>
          <block type="sirenia_api_excel_updatecell">
            <value name="PATH">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
            <value name="SHEET">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
            <value name="CELL">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
          </block>
          <block type="sirenia_api_excel_updatecells">
            <value name="PATH">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
            <value name="SHEET">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
            <value name="CELL">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
          </block>
        </category>
        <category name="File System" :colour="apiColor">
          <block type="sirenia_api_fs_ls">
            <value name="PATH">
              <shadow type="text"><field name="TEXT">E:\example\*.txt</field></shadow>
            </value>
          </block>
          <block type="sirenia_api_fs_tmpfolder"></block>
          <block type="sirenia_api_fs_tmpfile"></block>
          <block type="sirenia_api_fs_write">
            <value name="PATH">
              <shadow type="text"><field name="TEXT">E:\example\file.txt</field></shadow>
            </value>
            <value name="TEXT"><shadow type="javascript_variable_access"></shadow></value>
          </block>
        </category>
        <category name="Ftp" :colour="apiColor">
          <block type="sirenia_api_ftp_read">
            <value name="URL">
              <shadow type="text"><field name="TEXT">ftp://example/path/file.txt</field></shadow>
            </value>
          </block>
          <block type="sirenia_api_ftp_write">
            <value name="URL">
              <shadow type="text"><field name="TEXT">ftp://example/path/file.txt</field></shadow>
            </value>
          </block>
        </category>
        <category name="Http" :colour="apiColor">
          <block type="sirenia_api_http_get"></block>
          <block type="javascript_variable_declaration">
            <statement name="declarations">
              <block type="javascript_variable_assign">
                <field name="variableName">reply</field>
                <value name="value">
                  <block type="sirenia_api_http_get">
                    <value name="URL"><shadow type="text"></shadow></value>
                  </block>
                </value>
              </block>
            </statement>
          </block>
          <block type="controls_if">
            <value name="IF0">
              <block type="logic_compare">
                <field name="OP">EQ</field>
                <value name="A">
                  <block type="javascript_property_get">
                    <value name="OBJECT">
                      <block type="javascript_variable_access">
                        <field name="variableName">reply</field>
                      </block>
                    </value>
                    <field name="PROPERTY">status</field>
                  </block>
                </value>
                <value name="B">
                  <block type="math_number"><field name="NUM">200</field></block>
                </value>
              </block>
            </value>
            <statement name="DO0"></statement>
          </block>
          <block type="sirenia_api_http_post"></block>
          <block type="javascript_variable_declaration">
            <statement name="declarations">
              <block type="javascript_variable_assign">
                <field name="variableName">reply</field>
                <value name="value">
                  <block type="sirenia_api_http_post">
                    <value name="URL"><shadow type="text"></shadow></value>
                  </block>
                </value>
              </block>
            </statement>
          </block>
          <block type="sirenia_api_http_put"></block>
          <block type="javascript_variable_declaration">
            <statement name="declarations">
              <block type="javascript_variable_assign">
                <field name="variableName">reply</field>
                <value name="value">
                  <block type="sirenia_api_http_put">
                    <value name="URL"><shadow type="text"></shadow></value>
                  </block>
                </value>
              </block>
            </statement>
          </block>
          <block type="sirenia_api_http_delete"></block>
          <block type="javascript_variable_declaration">
            <statement name="declarations">
              <block type="javascript_variable_assign">
                <field name="variableName">reply</field>
                <value name="value">
                  <block type="sirenia_api_http_delete">
                    <value name="URL"><shadow type="text"></shadow></value>
                  </block>
                </value>
              </block>
            </statement>
          </block>
        </category>
        <category name="Notifications" :colour="apiColor">
          <block type="sirenia_api_notifications_show">
            <value name="NAME">
              <shadow type="text"><field name="TEXT">id</field></shadow>
            </value>
            <value name="HEADER">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
            <value name="MESSAGE">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
          </block>
          <block type="sirenia_api_notifications_update">
            <value name="NAME">
              <shadow type="text"><field name="TEXT">id</field></shadow>
            </value>
            <value name="HEADER">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
            <value name="MESSAGE">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
          </block>
          <block type="sirenia_api_notifications_close">
            <value name="NAME">
              <shadow type="text"><field name="TEXT">id</field></shadow>
            </value>
          </block>
        </category>
        <category name="Processes" :colour="apiColor">
          <block type="sirenia_api_processes_all"></block>
          <block type="sirenia_api_processes_current"></block>
          <block type="sirenia_api_processes_spawn">
            <value name="CMD">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
          </block>
        </category>
        <category name="Timer" :colour="apiColor">
          <block type="sirenia_api_timer_start">
            <value name="NAME">
              <shadow type="text"><field name="TEXT">timer</field></shadow>
            </value>
          </block>
          <block type="sirenia_api_timer_log">
            <value name="MESSAGE">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
            <value name="NAME">
              <shadow type="text"><field name="TEXT">timer</field></shadow>
            </value>
          </block>
          <block type="sirenia_api_timer_stop">
            <value name="NAME">
              <shadow type="text"><field name="TEXT">timer</field></shadow>
            </value>
          </block>
        </category>
        <category name="Xml" :colour="apiColor">
          <block type="sirenia_api_xml_load">
            <value name="XML_TEXT">
              <shadow type="text"><field name="TEXT">&lt;xml&gt;&lt;/xml&gt;</field></shadow>
            </value>
          </block>
          <block type="sirenia_api_xml_load_from">
            <value name="XML_URL">
              <shadow type="text">
                <field name="TEXT">http://example.com/path/file.xml</field>
              </shadow>
            </value>
          </block>
        </category>
        <category name="Wait" :colour="apiColor">
          <block type="sirenia_api_wait"></block>
          <block type="sirenia_api_wait_field"></block>
        </category>
        <category name="Window" :colour="apiColor">
          <block type="sirenia_api_window_dim">
            <value name="AMOUNT">
              <shadow type="math_number"><field name="NUM">100</field></shadow>
            </value>
          </block>
          <block type="sirenia_api_window_focus"></block>
          <block type="sirenia_api_window_get_title"></block>
          <block type="sirenia_api_window_is_maximized"></block>
          <block type="sirenia_api_window_is_minimized"></block>
          <block type="sirenia_api_window_is_modal_shown"></block>
          <block type="sirenia_api_window_is_title_shown">
            <value name="TITLE">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
          </block>
          <block type="sirenia_api_window_maximize"></block>
          <block type="sirenia_api_window_minimize"></block>
          <block type="sirenia_api_window_restore"></block>
          <block type="sirenia_api_window_sendkeys">
            <value name="KEYS">
              <shadow type="text"><field name="TEXT"></field></shadow>
            </value>
          </block>
        </category>
        <category name="Windows" :colour="apiColor">
          <block type="sirenia_api_windows_all"></block>
          <block type="sirenia_api_windows_for_app"></block>
          <block type="sirenia_api_windows_primary"></block>
        </category>
        <category name="Misc" :colour="apiColor">
          <block type="sirenia_api_guid_get"></block>
          <block type="javascript_variable_declaration">
            <statement name="declarations">
              <block type="javascript_variable_assign">
                <field name="variableName">guid</field>
                <value name="value"><block type="sirenia_api_guid_get"></block></value>
              </block>
            </statement>
          </block>
        </category>
      </category>
      <sep></sep>
      <category name="Field actions" colour="#8da65b">
        <block type="sirenia_api_edit_cell">
          <value name="rowMatch">
            <shadow type="text"><field name="TEXT"></field></shadow>
          </value>
          <value name="colMatch">
            <shadow type="text"><field name="TEXT"></field></shadow>
          </value>
          <value name="value">
            <shadow type="text"><field name="TEXT"></field></shadow>
          </value>
        </block>
        <block type="sirenia_api_click_cell">
          <value name="rowMatch">
            <shadow type="text"><field name="TEXT"></field></shadow>
          </value>
          <value name="colMatch">
            <shadow type="text"><field name="TEXT"></field></shadow>
          </value>
        </block>
        <block type="sirenia_api_field_click"></block>
        <block type="sirenia_api_field_simulate_click"></block>
        <block type="sirenia_api_field_read"></block>
        <block type="sirenia_api_field_inspect"></block>
        <block type="sirenia_api_field_select">
          <value name="VALUE">
            <shadow type="text"><field name="TEXT"></field></shadow>
          </value>
        </block>
        <block type="sirenia_api_field_select_index">
          <value name="INDEX">
            <shadow type="math_number"><field name="NUM"></field></shadow>
          </value>
        </block>
        <block type="sirenia_api_field_input">
          <value name="VALUE">
            <shadow type="text"><field name="TEXT"></field></shadow>
          </value>
        </block>
        <block type="sirenia_api_field_input_native">
          <value name="VALUE">
            <shadow type="text"><field name="TEXT"></field></shadow>
          </value>
        </block>
        <block type="sirenia_api_field_exists"></block>
        <block type="sirenia_api_field_highlight"></block>
        <block type="sirenia_api_field_lowlight"></block>
        <block type="sirenia_api_dynamic_field">
          <value name="fieldPath">
            <shadow type="text"><field name="TEXT"></field></shadow>
          </value>
        </block>
      </category>
      <category name="App fields 🏷" custom="FIELDS_CATEGORY" colour="#8da65b"></category>
      <category name="App flows ⤷" custom="FLOWS_CATEGORY" colour="#a65ba6"></category>
      <category name="App modules" custom="MODULES_CATEGORY" colour="#a6995b"></category>
      <sep></sep>
      <category name="Ext flows ⤷" custom="EXT_FLOWS_CATEGORY" colour="#a65ba6"></category>
      <category name="Ext Modules" custom="EXT_MODULES_CATEGORY" colour="#a6995b"></category>
    </xml>
  </div>
</template>

<style lang="scss">
// Not scoped because we are styling elements of the blockly editor
@charset "utf-8";
@import '../../../scss/variables';

#blocklyParent {
  width: 100%;
  height: 100%;

  #blocklyDiv {
    height: 100%;
  }

  .blocklyToolboxDiv {
    z-index: 1;
  }

  .blocklyMinimalBody {
    min-width: 0;
  }

  .blocklyFlyout {
    z-index: 0;
  }
}

#blocklyDisableOverlay {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
  opacity: 0.5;
  background-color: white;
}
</style>

<script>
// @flow
import Blockly from 'blockly/browser'
import '../../../blockly/CustomBlocks'
import DynamicCategories from '../../../blockly/DynamicCategories'
import { Field, Flow } from 'services'
import { take } from 'rxjs/operators'
import _ from 'lodash'
import { mapFields } from 'vuex-map-fields'
import { mapGetters } from 'vuex'

export default {
  name: 'blockly-editor',
  components: {},

  props: ['appId', 'flowId'],

  computed: {
    ...mapFields('flowEditor', { xml: 'blocklyXml' }),
    ...mapGetters('flowEditor', ['isDarkThemeActive']),
    ...mapGetters(['userPrefsInitialized']),
    readonly() {
      return this.$store.state.flowEditor.isFlowEditingDisabled
    }
  },

  data() {
    return {
      workspace: null,
      lastEmission: {},
      apiColor: '#7ecec7',
      initializing: true,
      encounteredRestoreError: '',
      throttledUpdate: _.throttle(
        () => {
          const code = Blockly.JavaScript.workspaceToCode(this.workspace)
          let xmlDom = Blockly.Xml.workspaceToDom(this.workspace)
          const xml = Blockly.Xml.domToText(xmlDom)
          if (code !== this.lastEmission.code || xml !== this.lastEmission.xml) {
            if (window.blocklyDebug) {
              console.debug('Saving xml ' + xml)
            }
            this.lastEmission = { code, xml }
            this.$emit('code-changed', this.lastEmission)
          }
        },
        500,
        { leading: false }
      )
    }
  },

  watch: {
    xml: {
      handler: function(val) {
        // This only gets invoked when the xml is changed from the parent component
        if (!this.workspace) return
        if (val && val !== this.lastEmission.xml) {
          this.restoreBlocksFromXml(val)
          this.workspace.clearUndo()
        }
      }
    },
    flowId: {
      handler: function(val, oldVal) {
        if (val && val !== oldVal) {
          Flow.readFlow(val).then(flow =>
            DynamicCategories.registerInputsCategoryCallback(this.workspace, flow.inputs)
          )
        }
      }
    },
    userPrefsInitialized() {
      this.initializeIfReady()
    }
  },

  methods: {
    workspaceUpdated(evt) {
      if (
        this.initializing ||
        this.readonly ||
        this.encounteredRestoreError ||
        !evt ||
        evt.type === 'ui'
      ) {
        return
      }
      this.throttledUpdate()
    },

    restoreBlocksFromXml(xml) {
      this.workspace.clear()
      if (xml) {
        try {
          let xmlDom = Blockly.Xml.textToDom(xml)
          Blockly.Xml.domToWorkspace(xmlDom, this.workspace)
        } catch (e) {
          this.encounteredRestoreError = e
          this.workspace.clear()
          console.warn('error restoring block diagram ' + e)
        }
      }
    },

    initializeMenu() {
      const promises = [
        // Dynamic categories
        Field.readFieldsForApp(this.appId).then(fields => {
          DynamicCategories.registerFieldsCategoryCallback(
            this.workspace,
            fields.map(f => f.name).sort()
          )
        }),

        Flow.readFlowsForApp(this.appId).then(flows => {
          DynamicCategories.registerFlowsCategoryCallback(this.workspace, flows, 'FLOWS_CATEGORY')
          DynamicCategories.registerModulesCategoryCallback(
            this.workspace,
            flows,
            'MODULES_CATEGORY'
          )
        }),

        Flow.readAllFlows().then(flows => {
          const externalFlows = flows.filter(f => f.application !== this.appId)
          DynamicCategories.registerFlowsCategoryCallback(
            this.workspace,
            externalFlows,
            'EXT_FLOWS_CATEGORY'
          )
          DynamicCategories.registerModulesCategoryCallback(
            this.workspace,
            externalFlows,
            'EXT_MODULES_CATEGORY'
          )
        })
      ]

      if (this.flowId) {
        promises.push(
          Flow.readFlow(this.flowId).then(flow =>
            DynamicCategories.registerInputsCategoryCallback(this.workspace, flow.inputs)
          )
        )
      }

      return Promise.all(promises)
    },

    initializeIfReady() {
      if (!this.userPrefsInitialized || this.workspace) return

      this.workspace = Blockly.inject(this.$refs.workspace, {
        toolbox: this.$refs.toolbox,
        zoom: { controls: true, wheel: true, startScale: 0.8 },
        grid: { spacing: 20, length: 3, colour: '#ccc', snap: true },
        oneBasedIndex: false,
        renderer: 'zelos' //'thrasos', 'geras', 'zelos'
      })
      // A color space that contains sirenia-blue (it's pretty crazy):
      // Blockly.HSV_SATURATION = 0.84
      // Blockly.HSV_VALUE = 0.82
      Blockly.COLLAPSE_CHARS = 50 // Default is 30
      Blockly.Constants.Loops.CONTROL_FLOW_IN_LOOP_CHECK_MIXIN.LOOP_TYPES.push(
        'javascript_for_loop'
      )
      Blockly.Constants.Loops.CONTROL_FLOW_IN_LOOP_CHECK_MIXIN.LOOP_TYPES.push(
        'javascript_for_of_loop'
      )

      // Handle updates
      this.workspace.addChangeListener(this.workspaceUpdated)

      this.initializeMenu()
      this.workspace.setTheme(this.isDarkThemeActive ? Blockly.Themes.Dark : Blockly.Themes.Modern)

      Blockly.svgResize(this.workspace)
      if (this.xml) {
        this.restoreBlocksFromXml(this.xml)
        this.workspace.clearUndo()
      }

      setTimeout(() => {
        // Ignore blockly change events for the first half second
        this.initializing = false
      }, 500)
    }
  },

  mounted() {
    this.initializeIfReady()
  }
}
</script>
